import type { SatMasteryBand } from '@/api/sat';

/**
 * The ONE status colour scale for mastery bands (and the legacy 0-4 report levels).
 *
 * Rules: green means proven only (Proficient and up). Developing is the same rose
 * everywhere, Approaching is amber, Warming up is sky, not started is neutral. Never
 * read a band colour from anywhere else; add a field here instead.
 * Domain identity colours (DOMAIN_COLORS in api/sat.ts) are a separate concern.
 */
export interface StatusTone {
  /** Solid fill for progress bars. */
  bar: string;
  /** Stroke for SVG progress rings. */
  stroke: string;
  /** Small solid dot next to a label. */
  dot: string;
  /** Text colour that stays readable on a card in light and dark. */
  text: string;
  /** Left accent on a tile. */
  accent: string;
  /** Raw colour for SVG/chart fills that cannot take a class. */
  solid: string;
  /** Soft pill: background, text and border. */
  pill: string;
}

export const STATUS_TONES: Record<SatMasteryBand, StatusTone> = {
  NOT_STARTED: {
    bar: 'bg-muted-foreground/20',
    stroke: 'stroke-muted-foreground/25',
    dot: 'bg-muted-foreground/40',
    text: 'text-muted-foreground',
    accent: 'border-l-muted-foreground/30',
    solid: '#a1a1aa',
    pill: 'bg-muted text-muted-foreground border-border',
  },
  INSUFFICIENT_EVIDENCE: {
    bar: 'bg-sky-400',
    stroke: 'stroke-sky-400',
    dot: 'bg-sky-400',
    text: 'text-sky-700 dark:text-sky-300',
    accent: 'border-l-sky-400',
    solid: '#38bdf8',
    pill: 'bg-sky-100 text-sky-900 border-sky-200 dark:bg-sky-500/15 dark:text-sky-200 dark:border-sky-500/30',
  },
  DEVELOPING: {
    bar: 'bg-rose-500',
    stroke: 'stroke-rose-500',
    dot: 'bg-rose-500',
    text: 'text-rose-700 dark:text-rose-300',
    accent: 'border-l-rose-500',
    solid: '#f43f5e',
    pill: 'bg-rose-100 text-rose-900 border-rose-200 dark:bg-rose-500/15 dark:text-rose-200 dark:border-rose-500/30',
  },
  APPROACHING: {
    bar: 'bg-amber-500',
    stroke: 'stroke-amber-500',
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    accent: 'border-l-amber-500',
    solid: '#f59e0b',
    pill: 'bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/30',
  },
  PROFICIENT: {
    bar: 'bg-emerald-500',
    stroke: 'stroke-emerald-500',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700 dark:text-emerald-300',
    accent: 'border-l-emerald-500',
    solid: '#10b981',
    pill: 'bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/30',
  },
  MASTERED_CANDIDATE: {
    bar: 'bg-emerald-600',
    stroke: 'stroke-emerald-600',
    dot: 'bg-emerald-600',
    text: 'text-emerald-800 dark:text-emerald-200',
    accent: 'border-l-emerald-600',
    solid: '#059669',
    pill: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/40',
  },
};

/** Legacy report levels 0-4 mapped onto the same bands (see LEVEL_LABELS). */
export function levelToBand(level: number): SatMasteryBand {
  if (level >= 4) return 'MASTERED_CANDIDATE';
  if (level === 3) return 'PROFICIENT';
  if (level === 2) return 'APPROACHING';
  if (level === 1) return 'DEVELOPING';
  return 'NOT_STARTED';
}

export const toneForLevel = (level: number): StatusTone => STATUS_TONES[levelToBand(level)];
